import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import lock from "../../assets/static/lock.svg";
import { BASE_URL } from "../../Helpers";

const DEFAULT_ALOHAR_WHATSAPP = "5492233018749";

const ENV_ENCRYPTION_KEY =
  process.env.REACT_APP_PROPERTY_HOST_PHONE_ENCRYPTION_KEY || "";

const hexToUint8Array = (hex) => {
  if (!hex || hex.length % 2 !== 0) {
    return null;
  }

  const bytes = new Uint8Array(hex.length / 2);

  for (let index = 0; index < hex.length; index += 2) {
    bytes[index / 2] = parseInt(hex.slice(index, index + 2), 16);
  }

  return bytes;
};

const decryptHostCellphone = async (encryptedCellphone) => {
  if (!encryptedCellphone || !ENV_ENCRYPTION_KEY || !window.crypto?.subtle) {
    return "";
  }

  const parts = encryptedCellphone.split(":");
  if (parts.length !== 2) {
    return "";
  }

  // Parte 1 → IV (hex → bytes)
  const ivBytes = hexToUint8Array(parts[0]);
  // Parte 2 → ciphertext (hex → bytes)
  const encryptedBytes = hexToUint8Array(parts[1]);

  if (!ivBytes || !encryptedBytes) {
    return "";
  }

  // key = SHA-256( PROPERTY_HOST_PHONE_ENCRYPTION_KEY como string UTF-8 )
  const keyMaterial = new TextEncoder().encode(ENV_ENCRYPTION_KEY);
  const keyHashBuffer = await window.crypto.subtle.digest("SHA-256", keyMaterial);

  const cryptoKey = await window.crypto.subtle.importKey(
    "raw",
    keyHashBuffer,
    { name: "AES-CBC" },
    false,
    ["decrypt"]
  );

  const decryptedBuffer = await window.crypto.subtle.decrypt(
    { name: "AES-CBC", iv: ivBytes },
    cryptoKey,
    encryptedBytes
  );

  return new TextDecoder("utf-8").decode(decryptedBuffer).trim();
};

const ButtonPayAlohador = ({
  housingId,
  housingTitle,
  guestName,
  persons,
  checkin,
  checkout,
  totalPrice,
  loading,
}) => {
  const [hostPhone, setHostPhone] = useState("");
  const [phoneLoading, setPhoneLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadHostPhone = async () => {
      if (!housingId) {
        if (isMounted) {
          setHostPhone(DEFAULT_ALOHAR_WHATSAPP);
          setPhoneLoading(false);
        }
        return;
      }

      try {
        setPhoneLoading(true);
        const apiKey = process.env.REACT_APP_ALOHAR_KEY;
        const response = await axios.get(
          `${BASE_URL}/housing/${housingId}?apiKey=${apiKey}`
        );
        const encryptedCellphone = response?.data?.user?.cellphone;

        if (!encryptedCellphone) {
          if (isMounted) {
            setHostPhone(DEFAULT_ALOHAR_WHATSAPP);
          }
          return;
        }

        const decryptedPhone = await decryptHostCellphone(
          encryptedCellphone
        );
        const normalizedPhone = decryptedPhone.replace(/\D/g, "");

        if (isMounted) {
          setHostPhone(normalizedPhone || DEFAULT_ALOHAR_WHATSAPP);
        }
      } catch (error) {
        if (isMounted) {
          setHostPhone(DEFAULT_ALOHAR_WHATSAPP);
        }
      } finally {
        if (isMounted) {
          setPhoneLoading(false);
        }
      }
    };

    loadHostPhone();

    return () => {
      isMounted = false;
    };
  }, [housingId]);

  const waUrl = useMemo(() => {
    if (!hostPhone) {
      return "#";
    }

    const message = encodeURIComponent(
      `Hola Alohar! Soy ${guestName} y quiero reservar el alohamiento "${housingTitle}".\n` +
        `Checkin: ${checkin}\n` +
        `Checkout: ${checkout}\n` +
        `Huéspedes: ${persons}\n` +
        `Total: $${totalPrice}`
    );

    return `https://wa.me/${hostPhone}/?text=${message}`;
  }, [checkin, checkout, guestName, hostPhone, housingTitle, persons, totalPrice]);

  const isDisabled = loading || phoneLoading || !hostPhone;

  return (
    <a
      className={isDisabled ? "Checkout__detail__disabled" : "Checkout__detail__btn"}
      href={waUrl}
      target="_blank"
      rel="noopener noreferrer"
      aria-disabled={isDisabled}
      onClick={(event) => {
        if (isDisabled) {
          event.preventDefault();
        }
      }}
    >
      <img src={lock} alt="Pagar al Alohador" />
      Pagar al Alohador
    </a>
  );
};

export default ButtonPayAlohador;
